// Utility functions for persisting month/year selection across page reloads

const STORAGE_KEY_PREFIX = 'jjt_selected_';

/**
 * Get the storage key for a specific component
 * @param {string} component - Component identifier (e.g., 'invoice_management', 'monthly_summary')
 * @returns {string} Storage key
 */
const getStorageKey = (component) => `${STORAGE_KEY_PREFIX}${component}`;

/**
 * Save selected month and year to localStorage
 * @param {string} component - Component identifier
 * @param {number} month - Selected month (1-12)
 * @param {number} year - Selected year
 */
export const saveMonthYear = (component, month, year) => {
  try {
    const data = { month, year, timestamp: Date.now() };
    localStorage.setItem(getStorageKey(component), JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save month/year to localStorage:', error);
  }
};

/**
 * Load selected month and year from localStorage
 * @param {string} component - Component identifier
 * @param {number} defaultMonth - Default month if none saved (1-12)
 * @param {number} defaultYear - Default year if none saved
 * @returns {object} Object with month and year properties
 */
export const loadMonthYear = (component, defaultMonth = null, defaultYear = null) => {
  try {
    const stored = localStorage.getItem(getStorageKey(component));
    if (stored) {
      const data = JSON.parse(stored);
      
      // Check if the stored data is not too old (older than 30 days)
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      if (data.timestamp && data.timestamp > thirtyDaysAgo) {
        // Validate the stored values
        if (data.month >= 1 && data.month <= 12 && data.year >= 2020 && data.year <= 2030) {
          return { month: data.month, year: data.year };
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load month/year from localStorage:', error);
  }
  
  // Return defaults if no valid stored data
  const now = new Date();
  return {
    month: defaultMonth || (now.getMonth() + 1),
    year: defaultYear || now.getFullYear()
  };
};

/**
 * Clear stored month/year for a component
 * @param {string} component - Component identifier
 */
export const clearMonthYear = (component) => {
  try {
    localStorage.removeItem(getStorageKey(component));
  } catch (error) {
    console.warn('Failed to clear month/year from localStorage:', error);
  }
};

/**
 * Get all stored month/year data (for debugging)
 * @returns {object} All stored data
 */
export const getAllStoredMonthYear = () => {
  const result = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const component = key.replace(STORAGE_KEY_PREFIX, '');
        const data = JSON.parse(localStorage.getItem(key));
        result[component] = data;
      }
    }
  } catch (error) {
    console.warn('Failed to get all stored month/year data:', error);
  }
  return result;
};